import { z } from 'zod';
import { max } from '#lib/utils/math.js';
import { mapOpt } from '#lib/overview/utils/object.js';
import { hasDef } from '#lib/overview/utils/types.js';
import { filter, map } from '#lib/utils/generators.js';
import { roundToPrecision } from '#lib/utils/number.js';
import type { DiffRange, TimeRange } from '#lib/server/aggregation.types.js';
import type { Snippet } from 'svelte';

export const condition = z.tuple([
  z.union([z.literal('<'), z.literal('<='), z.literal('>='), z.literal('>')]),
  z.number(),
]);
export type Condition = z.infer<typeof condition>;
export const conditionChecker = ([rel, bound]: Condition): ((value: number) => boolean) => {
  // prettier-ignore
  switch (rel) {
        case '<': return v => v < bound;
        case '<=': return v => v <= bound;
        case '>': return v => v > bound;
        case '>=': return v => v >= bound;
    }
};

const aggregationBase = z.union([
  z.literal('avg'),
  z.literal('sum'),
  z.literal('max'),
  z.literal('min'),
  z.object({ avgIf: condition }),
  z.object({ sumIf: condition }),
  z.object({ avgWeightedByColumn: z.string() }),
  z.literal('none'),
]);

export const aggregationStrategy = aggregationBase.or(
  z.object({
    grouping: aggregationBase,
    sorting: aggregationBase,
  }),
);

export type AggregationStrategy = z.infer<typeof aggregationStrategy>;
export const aggregateColumn = (
  strategy: AggregationStrategy,
  what: { key: string; peak?: boolean },
  arr: Iterable<TableRow>,
  purpose: 'grouping' | 'sorting',
): number => {
  if (typeof strategy === 'object' && purpose in strategy)
    strategy = strategy[purpose as keyof typeof strategy];
  if (strategy === 'none') return Number.NaN;
  const { key, peak } = what;
  const rowToValue = (r: TableRow) =>
    r.data !== undefined
      ? peak
        ? r.data[key]?.peak
        : r.data[key]?.value
      : r.type === 'group'
        ? aggregateColumn(strategy, what, r.children, purpose)
        : undefined;

  const rowToWeight: (r: TableRow) => number = hasDef(strategy, 'avgWeightedByColumn')
    ? // FIXME: not sure what to do for subgroups
      (r) => r.data?.[strategy.avgWeightedByColumn]?.value ?? 0
    : () => 1;
  const isNum = (x: any): x is number => typeof x === 'number';

  if (strategy === 'max')
    return Math.max(...(filter(map(arr, rowToValue), isNum) as Iterable<number>));
  if (strategy === 'min')
    return Math.min(...(filter(map(arr, rowToValue), isNum) as Iterable<number>));

  // prettier-ignore
  const cond = mapOpt(
        hasDef(strategy, "avgIf") ? strategy.avgIf :
        hasDef(strategy, "sumIf") ? strategy.sumIf : undefined,
        conditionChecker,
        () => true
    );

  arr = filter(
    filter(arr, (r) => mapOpt(rowToValue(r), cond, false)),
    (r) => r.data !== undefined,
  );
  let sum = 0;
  let totalWeight = 0;
  for (const row of arr) {
    const value = rowToValue(row);
    const weight = rowToWeight(row);
    sum += weight * (value ?? 0);
    totalWeight += weight;
  }

  if (strategy === 'sum' || hasDef(strategy, 'sumIf')) return sum;
  if (strategy === 'avg' || hasDef(strategy, 'avgIf') || hasDef(strategy, 'avgWeightedByColumn'))
    return sum / totalWeight;

  throw new Error('Internal error, unhandled aggregation strategy: ' + JSON.stringify(strategy));
};

export const thresholdSchema = z.object({
  urgency: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  if: condition,
});
export type Threshold = z.infer<typeof thresholdSchema>;

type ThresholdLevel = 0 | 1 | 2 | 3;
export const testThresholds = (thresholds: Threshold[], value: number): ThresholdLevel => {
  value = roundToPrecision(value, 2);
  let result: ThresholdLevel = 0;

  for (const {
    urgency,
    if: [cond, v],
  } of thresholds) {
    if (
      (cond === '<' && value < v) ||
      (cond === '<=' && value <= v) ||
      (cond === '>=' && value >= v) ||
      (cond === '>' && value > v)
    ) {
      if (urgency > result) result = urgency;
    }
  }
  return result;
};
export const mostSevereAlert = (cols: FlatColumns, r: TableRow): ThresholdLevel => {
  if (r.data !== undefined) {
    return cols
      .filter(
        (c): c is Extract<typeof c, { thresholds?: any }> =>
          'thresholds' in c && r.data![c.key] !== undefined,
      )
      .reduce(
        (acc, c) => max(acc, testThresholds(c.thresholds ?? [], r.data![c.key].value)),
        0 as ThresholdLevel,
      );
  }
  switch (r.type) {
    case 'group':
      return r.children.reduce((acc, c) => max(acc, mostSevereAlert(cols, c)), 0 as ThresholdLevel);
    case 'lazyGroup':
      return 0; // TODO
    default:
      return 0;
  }
};

const tableRowCore = z.object({
  name: z.string(),
  tags: z.array(z.string()).default([]),
  parameters: z.record(z.string(), z.unknown()).default({}),
});

export const leafData = z.record(
  z.string(),
  z.object({
    units: z.string().optional(),
    value: z.number().or(z.nan()),
    peak: z.number().optional(),
    date: z.date().optional(),
  }),
);

export const tableRowLeaf = tableRowCore
  .extend({
    data: leafData,
    type: z.literal('leaf'),
  })
  .strict();

export const tableRowGroup = tableRowCore.extend({
  type: z.literal('group'),
  get children() {
    return tableRow.array();
  },
  data: leafData.optional(),
});

export type CountHintContent = {
  subgroups?: number;
  leafs: number;
};

export const tableRowGroupLazy = tableRowCore.extend({
  type: z.literal('lazyGroup'),
  get children() {
    return tableRow.array().optional();
  },
  countHint: z
    .object({
      subgroups: z.number().optional(),
      leafs: z.number(),
    })
    .optional(),
  data: leafData.optional(),
});

export const tableRow = tableRowLeaf.or(tableRowGroup).or(tableRowGroupLazy);
const columnBase = z.object({
  title: z.string(),
  description: z.string().optional(),
  customCellTooltip: z.custom<Snippet<[TableRow, any]>>().optional(),
  width: z.number().or(z.string()).optional(),
});

const operation = z.string().refine((val) => {
  const parts = val.split(' ');
  if (parts.length !== 3) return false;

  const [_, op] = parts;

  if (!['+', '-', '*', '/'].includes(op)) return false;

  return true;
}, 'should be in "<metric> <op> <metric>" format"');

// ! this is not a discriminated union, because type: "data" is optional
export const column = z.union([
  columnBase.extend({
    type: z.literal('data').optional(),
    key: z.string(),
    aggregationStrategy,
    thresholds: thresholdSchema.array().optional(),
    isPrediction: z.boolean().optional(),
    hideInBadge: z.boolean().optional(),
    changeScale: z.enum(['short', 'long']).optional(),
    numberFormat: z.custom<Intl.NumberFormatOptions>().optional(),
    calculation: z
      .union([
        z
          .object({
            type: z.literal('localOp'),
            op: operation,
            locals: z.record(z.string(), z.string()).optional(),
            units: z.string().optional(),
            mul: z.number().optional(),
          })
          .transform((val) => {
            const parts = val.op.split(' ');
            const [lhs, op, rhs] = parts;

            return {
              ...val,
              a: lhs,
              op: op as '+' | '-' | '*' | '/',
              b: rhs,
            };
          }),
        z.object({
          type: z.literal('timeDiff'),
          metric: z.string(),
          timeRange: z.custom<DiffRange>(),
        }),
        z.object({
          type: z.literal('prediction'),
          expr: z.string(),
          timeRange: z.custom<TimeRange>(),
          target: z.number(),
        }),
        z.object({
          type: z.literal('expression'),
          expr: z.string(),
        }),
      ])
      .optional(),
  }),
  columnBase.extend({
    type: z.literal('hasTag'),
    tag: z.string(),
  }),
  columnBase.extend({
    type: z.literal('parameter'),
    parameter: z.string(),
    render: z.function({ input: z.tuple([z.unknown()]), output: z.string() }).optional(),
  }),
  columnBase.extend({
    type: z.literal('custom'),
    key: z.string().optional(),
    snippet: z.custom<Snippet<[row: TableRow, props?: any]>>(),
    props: z.any().optional(),
  }),
  columnBase.extend({
    type: z.literal('date-like-parameter'),
    parameter: z.string(),
    displayFormat: z.string().default('HH:mm:ss DD.MM.YYYY'),
  }),
]);
export type Column = z.infer<typeof column>;

export const columnGroup = z.object({
  title: z.string(),
  columns: column.array(),
});
export type ColumnGroup = z.infer<typeof columnGroup>;

export const columnsSchema = column.or(columnGroup).array();
export type Columns = z.infer<typeof columnsSchema>;
export type ColumnsInput = z.input<typeof columnsSchema>;

export const flatColumnsSchema = column.and(z.object({ group: columnGroup.optional() })).array();
export type FlatColumns = z.infer<typeof flatColumnsSchema>;

export type TableRowLeaf = z.infer<typeof tableRowLeaf>;
export type TableRowGroup = z.infer<typeof tableRowGroup>;
export type TableRowLazyGroup = z.infer<typeof tableRowGroupLazy>;
export type TableRow = z.infer<typeof tableRow>;

export const tableSchema = tableRow.array();
export type TableData = z.infer<typeof tableSchema>;

export const twoLayerTable = tableRowGroup
  .omit({
    children: true,
  })
  .extend({
    children: tableRowLeaf.array(),
  })
  .array();
